// Analyst answers arrive as light Markdown. One parser feeds the on-screen
// renderer AND the Excel / PDF / email exporters, so files match the screen.

const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isRule = (l) => /^\s*\|[\s:|-]+\|\s*$/.test(l);
const cells = (row) => row.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
const LIST = /^\s*([-*•]|\d+\.)\s+/;
const KPI = /^\s*(?:[-*•]\s+)?`?\**KPI\**:\s*\**(.+?)\s*=\s*(.+?)\**`?\s*$/i;

// → [{type: "kpis"|"heading"|"text"|"method"|"list"|"table", text?, items?, columns?, rows?}]
// "KPI: label = value" lines (max 3) become headline tiles.
export function parseAnswer(text) {
  const out = [];
  const lines = String(text || "").split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (KPI.test(l)) {
      const items = [];
      while (i < lines.length && KPI.test(lines[i])) {
        const [, label, value] = lines[i++].match(KPI);
        items.push({ label: label.replace(/\*\*/g, "").trim(), value: value.replace(/\*\*/g, "").trim() });
      }
      i--;
      out.push({ type: "kpis", items: items.slice(0, 3) });
    } else if (isTableRow(l)) {
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) rows.push(lines[i++]);
      i--;
      const body = rows.filter((r) => !isRule(r)).map(cells);
      if (body.length) out.push({ type: "table", columns: body[0], rows: body.slice(1) });
    } else if (LIST.test(l)) {
      const items = [];
      while (i < lines.length && LIST.test(lines[i])) items.push(lines[i++].replace(LIST, ""));
      i--;
      out.push({ type: "list", items });
    } else if (/^#{1,6}\s+/.test(l)) {
      out.push({ type: "heading", text: l.replace(/^#{1,6}\s+/, "") });
    } else if (/^\s*\**method:?\**/i.test(l)) {
      out.push({ type: "method", text: l.replace(/\*\*/g, "").trim() });
    } else if (l.trim()) {
      out.push({ type: "text", text: l.trim() });
    }
  }
  return out;
}

// Exporters want plain blocks: the backend strips links/bold itself; "method"
// travels as text so customer PDFs can drop it.
export const exportBlocks = (blocks) =>
  blocks.map((b) => (b.type === "method" ? { type: "text", text: b.text } : b));

const plain = (s) => String(s ?? "").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/\*\*/g, "").replace(/`/g, "");
const esc = (s) => plain(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function tableToTsv(t) {
  return [t.columns, ...t.rows].map((r) => r.map((c) => plain(c).replace(/\t/g, " ")).join("\t")).join("\n");
}

export function tableToHtml(t) {
  const th = t.columns.map((c) => `<th style="background:#f97316;color:#fff;text-align:left;padding:4px 8px">${esc(c)}</th>`).join("");
  const trs = t.rows.map((r) => `<tr>${r.map((c) => `<td style="border-top:1px solid #e4e4e7;padding:4px 8px">${esc(c)}</td>`).join("")}</tr>`).join("");
  return `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px"><tr>${th}</tr>${trs}</table>`;
}

// Copy as rich HTML (pastes as a real table into Outlook / Excel / Docs) with
// a TSV fallback for plain-text targets.
export async function copyRich(html, text) {
  try {
    if (window.ClipboardItem && navigator.clipboard?.write) {
      await navigator.clipboard.write([new window.ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      })]);
      return true;
    }
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function answerToCopy(blocks) {
  const html = blocks.map((b) => {
    if (b.type === "table") return tableToHtml(b);
    if (b.type === "list") return `<ul>${b.items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`;
    if (b.type === "kpis") return `<p>${b.items.map((k) => `<b>${esc(k.label)}:</b> ${esc(k.value)}`).join(" &nbsp;·&nbsp; ")}</p>`;
    if (b.type === "heading") return `<h3>${esc(b.text)}</h3>`;
    return `<p>${esc(b.text)}</p>`;
  }).join("");
  const text = blocks.map((b) => {
    if (b.type === "table") return tableToTsv(b);
    if (b.type === "list") return b.items.map((i) => `• ${plain(i)}`).join("\n");
    if (b.type === "kpis") return b.items.map((k) => `${k.label}: ${k.value}`).join("\n");
    return plain(b.text);
  }).join("\n\n");
  return { html, text };
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function* readSse(res) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const line = chunk.split("\n").find((x) => x.startsWith("data: "));
      if (line) {
        try { yield JSON.parse(line.slice(6)); } catch { /* partial line */ }
      }
    }
  }
}
